<template>
    <OpportunityPageDetailTopLayout>
        <template #image>
            <CThumbnailImage
                :alt="`Príležitosť ${skill.title}`"
                class="rounded-md text-gray-750 dark:text-txt-headings"
                :src="skill.image"
                type="skill"
            />
            <div
                v-if="skill.recordingURL"
                class="absolute -left-2 top-2 flex h-5 w-18 items-center justify-center bg-red-500 text-sm font-semibold text-white"
            >
                ZÁZNAM
            </div>
        </template>

        <template #highlights>
            <CHeading class="mb-4">
                {{ skill.title }}
            </CHeading>

            <LayoutTopOrgProfileLink :org="org" show-img-on-desktop />
            <!-- Target Group -->
            <IconTextRow v-if="targetGroup" :icon="'group'" variant="default">
                {{ capitalize(targetGroup) }}
            </IconTextRow>

            <IconTextRow v-if="skill.desc" icon="quote" variant="default">
                <span v-html="skill.desc"></span>
            </IconTextRow>

            <!-- Format/Type -->
            <IconTextRow v-if="skill.format" icon="cooperation" variant="default">
                {{ skill.format }}
            </IconTextRow>

            <!-- Location -->
            <IconTextRow v-if="skill.placeOnline || skill.placeAddress" icon="location" variant="default">
                {{ skill.placeOnline || skill.placeAddress }}
            </IconTextRow>

            <!-- Price -->
            <IconTextRow v-if="skill.price !== null && skill.price !== undefined" icon="price" variant="default">
                {{ skill.price === 0 ? 'Bezplatne' : `${skill.price}€` }}
            </IconTextRow>

            <template v-if="skill.topics && skill.topics.length > 0">
                <CSearchTags
                    class="mt-2 sm:mt-8"
                    query-search-param="topics"
                    search-route-name="vzdelavanie"
                    :tags="skill.topics"
                />
            </template>
        </template>

        <template #bottom>
            <SkillViewParameters :skill="skill" />
        </template>
    </OpportunityPageDetailTopLayout>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import { computed } from 'vue'

import type { Skill, SkillOrgPreview } from '#shared/types/Skill'
import IconTextRow from '@/components/common/IconTextRow.vue'
import LayoutTopOrgProfileLink from '@/components/common/LayoutTopOrgProfileLink.vue'
import OpportunityPageDetailTopLayout from '@/components/common/opportunity/OpportunityPageDetailTopLayout.vue'
import SkillViewParameters from '@/components/skills/SkillViewParameters.vue'
import CHeading from '@/components/ui/CHeading.vue'
import CSearchTags from '@/components/ui/CSearchTags.vue'
import CThumbnailImage from '@/components/ui/CThumbnailImage.vue'
import { whoIdToLabelMap } from '~~/i18n/mentoring/sk/expertise'

const props = defineProps({
    skill: {
        type: Object as PropType<Skill>,
        required: true
    },
    org: {
        type: Object as PropType<SkillOrgPreview>,
        required: true
    }
})

const targetGroup = computed(() => {
    if (!props.skill.who || props.skill.who.length === 0) {
        return null
    }
    
    const labels = props.skill.who
        .map((item) => whoIdToLabelMap[item]?.groupInfinitiveLabel)
        .filter(Boolean) // Remove undefined/null values
    
    return labels.length > 0 ? labels.join(', ') : null
})

function capitalize(str: string): string {
    if (!str) return ''
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}
</script>
